import type {QueryClient} from '@github-ui/react-query'
import {type JSONRequestInit, verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {SavedView} from '../components/views/types'
import {LABELS} from '../constants/labels'
import {VALUES} from '../constants/values'
import {parseFetchErrorMessage, SAVED_VIEW_QUERY_KEY, SAVED_VIEWS_QUERY_KEY} from '../queries/saved-views'

export interface SavedViewMutationOptions {
  owner: string
  repo: string
}

interface SavedViewCacheTargets extends SavedViewMutationOptions {
  queryClient: QueryClient
}

/** Error thrown by saved-view mutations that preserves the HTTP `status` so callers can branch on it. */
export class SavedViewMutationError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'SavedViewMutationError'
    this.status = status
  }
}

export function isPinLimitError(error: unknown, attemptedPin: boolean): boolean {
  // The pin endpoint currently returns `400` only for the pinned-per-repository cap
  // (RepositorySearchShortcut::MAX_PINNED_PER_REPOSITORY). If the endpoint begins
  // validating different things, this will be a mislabeled error; switch to using
  // error strings in that case.
  return attemptedPin && error instanceof SavedViewMutationError && error.status === 400
}

export function pinnedMutationErrorMessage(error: unknown, attemptedPin: boolean): string {
  if (isPinLimitError(error, attemptedPin)) return LABELS.views.pinLimitReached(VALUES.maxPinnedViews)
  return attemptedPin ? LABELS.views.pinError : LABELS.views.unpinError
}

export async function sendSavedViewMutation(
  path: string,
  init: JSONRequestInit,
  errorMessage: string,
): Promise<Response> {
  const response = await verifiedFetchJSON(path, init)
  if (!response.ok) {
    throw new SavedViewMutationError(response.status, await parseFetchErrorMessage(response, errorMessage))
  }
  return response
}

export async function sendSavedViewMutationJson<T>(
  path: string,
  init: JSONRequestInit,
  errorMessage: string,
): Promise<T> {
  const response = await sendSavedViewMutation(path, init, errorMessage)
  return (await response.json()) as T
}

export function primeSavedViewCaches({queryClient, owner, repo}: SavedViewCacheTargets, savedView: SavedView): void {
  queryClient.setQueryData([SAVED_VIEW_QUERY_KEY, owner, repo, String(savedView.id)] as const, {
    savedView,
    viewerCanManageViews: true,
  })
  invalidateSavedViewsList({queryClient, owner, repo})
}

export function invalidateSavedViewsList({queryClient, owner, repo}: SavedViewCacheTargets): void {
  void queryClient.invalidateQueries({queryKey: [SAVED_VIEWS_QUERY_KEY, owner, repo]})
}

export function removeSavedViewCaches({queryClient, owner, repo}: SavedViewCacheTargets, id: number | string): void {
  queryClient.removeQueries({queryKey: [SAVED_VIEW_QUERY_KEY, owner, repo, String(id)] as const})
  invalidateSavedViewsList({queryClient, owner, repo})
}
